import { Branding } from "@bcm-dev/bcms-vite";
import CountriesCustomSelect from "components/custom-ui/CountriesCustomSelect";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import StatesCustomSelect from "components/custom-ui/StatesCustomSelect";
import { FormContainer, FormRowView } from "components/shared";
import { UITextInput } from "components/ui";
import React from "react";

/**
 * @function WireinstitutionInformationWidget
 * @param {STRING} className - Allow us to set class names
 * @param {STRING} institutionName - Sets the institution name text.
 * @param {STRING} institutionNumber - 	Sets the institution number text.
 * @param {STRING} institutionAddressLineOne - Sets the institution address line one text.
 * @param {STRING} institutionAddressLineTwo - Sets the institution address line two text.
 * @param {STRING} institutionCity - Sets the institution city text.
 * @param {STRING} institutionZip - Sets the institution zip code text. 
 * @param {STRING} institutionPostalCode - Sets the institution postal code text.
 * @param {STRING} institutionStateOrProvince - Sets the institution state or province text
 * @return {React.Component}
 * @exported true
 * @description This is the wire financial institution form used for OneTimeWirePage
 */
export default function WireFinancialInstitutionForm(props) {
    const sectionType = props.sectionType ?? "financialInstitution";
    const data = props.data ?? {};
    const ui = props.ui ?? {};
    const action = props.action ?? {};
    const oneTimeWireTransfer = data.oneTimeWireTransfer ?? {};
    const sendEmailWhenWireStatusDownloaded = oneTimeWireTransfer.sendEmailWhenWireStatusDownloaded ?? false;
    const payeeType = oneTimeWireTransfer.payeeType ? oneTimeWireTransfer.payeeType.toLowerCase() : undefined;
    const prop = `${ sectionType }NumberType`;

    const className = props.className ?? "";
    const institutionName = props.institutionName ?? "[institutionName]";
    const institutionNumber = props.institutionNumber ?? "[institutionNumber]";
    const institutionAddressLineOne = props.institutionAddressLineOne ?? "[institutionAddressLineOne]";
    const institutionAddressLineTwo = props.institutionAddressLineTwo ?? "[institutionAddressLineTwo]";
    const institutionCity = props.institutionCity ?? "[institutionCity]";
    const institutionStateOrProvince = props.institutionStateOrProvince ?? "[institutionStateOrProvince]";
    const institutionZip = props.institutionZip ?? "[institutionZip]";
    const institutionPostalCode = props.institutionPostalCode ?? "[institutionPostalCode]";
    const numberTypeSelectIsActive = ui[`${ sectionType }NumberTypeSelectIsActive`] ?? false;
    const stateSelectIsActive = ui[`${ sectionType }StateSelectIsActive`] ?? false;
    const countrySelectIsActive = ui[`${ sectionType }CountrySelectIsActive`] ?? false;
    
    return (
        <div className={`wire-financial-institution-form ${className}`}>
            <FormContainer className="small-form-width">
                <FormRowView className="institution-number-type" text="* Institution Number Type">
                { payeeType === "domestic" && 
                    <CustomSelect active={ numberTypeSelectIsActive }  value={ oneTimeWireTransfer[`${ sectionType }NumberType`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }NumberType`) }>
                        <CustomSelectItem value="aba">ABA</CustomSelectItem>
                    </CustomSelect>
                }
                { payeeType !== "domestic" && 
                    <CustomSelect active={ numberTypeSelectIsActive } value={ oneTimeWireTransfer[`${ sectionType }NumberType`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }NumberType`) }>
                        <CustomSelectItem value="aba">ABA</CustomSelectItem>
                        <CustomSelectItem value="bic">BIC</CustomSelectItem>
                    </CustomSelect>
                }
                </FormRowView>
                <FormRowView className="institution-name" text="* Institution Name">
                    <UITextInput value={ oneTimeWireTransfer[`${ sectionType }Name`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }Name`) } />
                </FormRowView>
                <FormRowView className="institution-number" text="* Institution Number">
                    <UITextInput value={ oneTimeWireTransfer[`${ sectionType }Number`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }Number`) } />
                </FormRowView>
                <FormRowView className="address-line-one" text="Address Line 1">
                    <UITextInput value={ oneTimeWireTransfer[`${ sectionType }AddressLine1`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }AddressLine1`) } />
                </FormRowView>
                <FormRowView className="address-line-two" text="Address Line 2">
                    <UITextInput value={ oneTimeWireTransfer[`${ sectionType }AddressLine2`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }AddressLine2`) } />
                </FormRowView>
                <FormRowView className="city" text="City">
                    <UITextInput value={ oneTimeWireTransfer[`${ sectionType }City`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }City`) } />
                </FormRowView>

                {/* Domestic form rows is shown if "Domestic" institution Type is selected */}
                { payeeType === "domestic" &&
                <div className="form-rows domestic-form-rows">
                    <FormRowView className="state" text="State">
                        <StatesCustomSelect active={ stateSelectIsActive } value={ oneTimeWireTransfer[`${ sectionType }State`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }State`) } />
                    </FormRowView>
                    <FormRowView className="zip-code" text="ZIP Code">
                        <UITextInput value={ oneTimeWireTransfer[`${ sectionType }Zip`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }Zip`) } />
                    </FormRowView>

                    <Branding id="beneficiaryref_abi22" value="true">
                        <FormRowView className="beneficiary-reference" text="Beneficiary Reference">
                            <UITextInput value={ oneTimeWireTransfer[`${ sectionType }BeneficiaryReference`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }BeneficiaryReference`) } />
                        </FormRowView>
                    </Branding>
                    <Branding id="messagetobeneficiary_abi22" value="true">
                        <FormRowView className="message-to-beneficiary" text="Message to Beneficiary">
                            <UITextInput value={ oneTimeWireTransfer[`${ sectionType }MessageToBeneficiary1`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }MessageToBeneficiary1`) } />
                        </FormRowView>
                        <FormRowView className="message-to-beneficiary">
                            <UITextInput value={ oneTimeWireTransfer[`${ sectionType }MessageToBeneficiary2`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }MessageToBeneficiary2`) } />
                        </FormRowView>
                        <FormRowView className="message-to-beneficiary">
                            <UITextInput value={ oneTimeWireTransfer[`${ sectionType }MessageToBeneficiary3`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }MessageToBeneficiary3`) } />
                        </FormRowView>
                        <FormRowView className="message-to-beneficiary">
                            <UITextInput value={ oneTimeWireTransfer[`${ sectionType }MessageToBeneficiary4`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }MessageToBeneficiary4`) } />
                        </FormRowView>
                    </Branding>
                </div>
                }

                {/* International form rows is shown if "International" institution Type is selected */}
                { payeeType === "international" &&
                <div className="form-rows international-form-rows">
                    <FormRowView className="state-or-province" text="State or Province">
                        <UITextInput value={ oneTimeWireTransfer[`${ sectionType }StateOrProvidence`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }StateOrProvidence`) } />
                    </FormRowView>
                    <FormRowView className="postal-code" text="Postal Code">
                        <UITextInput value={ oneTimeWireTransfer[`${ sectionType }PostalCode`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }PostalCode`) } />
                    </FormRowView>
                    <FormRowView className="country" text="* Country">
                        <CountriesCustomSelect active={ countrySelectIsActive } value={ oneTimeWireTransfer[`${ sectionType }Country`] } onChange={ action.onChangeOneTimeWireProp(`${ sectionType }Country`) } />
                    </FormRowView>
                </div>
                }
            </FormContainer>
        </div>

    )
}
